import { Injectable } from '@angular/core';
import { PdfFitMode, PdfRotation } from '../models/pdf-viewer.model';

/** Intrinsic page size at scale 1, rotation 0. */
export interface PageSize {
  width: number;
  height: number;
}

/** Size of the scrollable viewport the page must fit into. */
export interface ViewportSize {
  width: number;
  height: number;
}

/**
 * Pure zoom mathematics — no state, no DOM. Kept separate so the fit/clamp/step rules are unit
 * testable in isolation and shared between the main view and the thumbnail strip.
 */
@Injectable({ providedIn: 'root' })
export class PdfZoomService {
  /** Padding (px) subtracted from the viewport so a fitted page never touches the edges. */
  private readonly gutter = 24;

  /** Clamp a zoom percentage into the `[min, max]` range. */
  clamp(zoom: number, min: number, max: number): number {
    return Math.min(max, Math.max(min, Math.round(zoom)));
  }

  /** Next zoom level when zooming in, respecting the step and ceiling. */
  zoomIn(zoom: number, step: number, max: number): number {
    return this.clamp(zoom * step, zoom, max);
  }

  /** Next zoom level when zooming out, respecting the step and floor. */
  zoomOut(zoom: number, step: number, min: number): number {
    return this.clamp(zoom / step, min, zoom);
  }

  /**
   * Resolve a fit mode to a concrete zoom percentage for a page within a viewport.
   * Accounts for rotation by swapping the page's width/height at 90°/270°.
   */
  fitToZoom(
    mode: PdfFitMode,
    page: PageSize,
    viewport: ViewportSize,
    rotation: PdfRotation,
    min: number,
    max: number
  ): number {
    const rotated = rotation === 90 || rotation === 270;
    const pageWidth = rotated ? page.height : page.width;
    const pageHeight = rotated ? page.width : page.height;

    const availableWidth = Math.max(1, viewport.width - this.gutter);
    const availableHeight = Math.max(1, viewport.height - this.gutter);

    const widthScale = (availableWidth / pageWidth) * 100;
    const pageScale = Math.min(widthScale, (availableHeight / pageHeight) * 100);

    let zoom: number;
    switch (mode) {
      case 'width':
        zoom = widthScale;
        break;
      case 'page':
        zoom = pageScale;
        break;
      case 'auto':
      default:
        // Never magnify past 100% automatically; only shrink to fit when needed.
        zoom = Math.min(100, widthScale);
        break;
    }
    return this.clamp(zoom, min, max);
  }

  /** Convert a zoom percentage to a PDF.js render scale. */
  toScale(zoom: number): number {
    return zoom / 100;
  }
}
